import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { theme } from 'components/ScrollableTable/theme';
import FilterPopover from 'components/ScrollableTable/FilterPopover/FilterPopover';
import DateFilterPopover from 'components/Inspect/DateFilterPopover/DateFilterPopover';
import OptionFilterPopover from 'components/Inspect/OptionFilterPopover/OptionFilterPopover';

export class InspectRecordsHeaderCell extends Component {
  constructor(props) {
    super(props);
    this.state = { popoverOpen: false, anchorRect: null, draftValue: '' };
    this.handleTitleClick = this.handleTitleClick.bind(this);
    this.handleFilterIconClick = this.handleFilterIconClick.bind(this);
    this.handlePopoverChange = this.handlePopoverChange.bind(this);
    this.handlePopoverSubmit = this.handlePopoverSubmit.bind(this);
    this.handleDateApply = this.handleDateApply.bind(this);
    this.handleOptionsApply = this.handleOptionsApply.bind(this);
    this.handlePopoverClose = this.handlePopoverClose.bind(this);
  }

  handleTitleClick() {
    var props = this.props;
    if (!props.sortable) return;
    var key = props.columnKey;
    var nextDirection;
    if (props.sortColumnKey !== key) {
      nextDirection = 'asc';
    } else if (props.sortDirection === 'asc') {
      nextDirection = 'desc';
    } else {
      nextDirection = null;
    }
    props.onSortChange(nextDirection === null ? null : key, nextDirection);
  }

  handleFilterIconClick(e) {
    e.stopPropagation();
    var rect = e.currentTarget.getBoundingClientRect();
    this.setState({
      popoverOpen: true,
      anchorRect: rect,
      draftValue:
        typeof this.props.filterValue === 'string'
          ? this.props.filterValue
          : '',
    });
  }

  handlePopoverChange(e) {
    this.setState({ draftValue: e.target.value });
  }

  handlePopoverSubmit(e, isClear) {
    if (e && e.stopPropagation) e.stopPropagation();
    var value = isClear ? '' : this.state.draftValue;
    this.props.onFilterChange(this.props.columnKey, value);
    this.setState({ popoverOpen: false, anchorRect: null, draftValue: value });
  }

  handleDateApply(value) {
    this.props.onFilterChange(this.props.columnKey, value);
    this.setState({ popoverOpen: false, anchorRect: null, draftValue: value });
  }

  handleOptionsApply(values) {
    this.props.onFilterChange(this.props.columnKey, values);
    this.setState({ popoverOpen: false, anchorRect: null });
  }

  handlePopoverClose() {
    this.setState({ popoverOpen: false, anchorRect: null });
  }

  render() {
    var props = this.props;
    var sortable = props.sortable;
    var filterable = props.filterable;
    var isSorted = sortable && props.sortColumnKey === props.columnKey;
    var hasFilter = filterable && !!props.filterValue;
    var titleStyle = isSorted ? theme.active : theme.headerTitle;
    var filterIconStyle = {
      fontSize: 16,
      color: hasFilter ? theme.active.color : theme.filter.color,
    };
    if (hasFilter) {
      filterIconStyle.fontVariationSettings = "'FILL' 1";
    }
    var sortIconName =
      props.sortDirection === 'desc' ? 'arrow_downward' : 'arrow_upward';

    return (
      <div
        className={
          'header-cell without-ordering' +
          (props.showHierarchy ? ' header-cell--with-hierarchy' : '')
        }
      >
        <div
          className="header-cell-info"
          onClick={this.handleTitleClick}
          title={props.title}
          style={Object.assign(
            { cursor: sortable ? 'pointer' : 'default' },
            titleStyle,
          )}
        >
          {props.showHierarchy &&
            (props.path ? (
              <span className="preview-table-column-hierarchy">
                {props.path}
              </span>
            ) : (
              <span className="preview-table-column-hierarchy-spacer" />
            ))}
          <div className="header-cell-title-row">
            <span className="header-cell-title">{props.label}</span>
            {props.attributeLabel && (
              <span className="header-cell-attribute">
                {props.attributeLabel}
              </span>
            )}
          </div>
        </div>
        <div className="header-cell-actions">
          {filterable && (
            <div
              className="header-cell-filter"
              onClick={this.handleFilterIconClick}
            >
              <i className="material-symbols-outlined" style={filterIconStyle}>
                filter_alt
              </i>
            </div>
          )}
          {isSorted && (
            <div
              className="header-cell-sort"
              style={theme.active}
              onClick={this.handleTitleClick}
            >
              <i
                className="material-symbols-outlined"
                style={{
                  fontSize: 16,
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: -1,
                  left: 5,
                }}
              >
                {sortIconName}
              </i>
            </div>
          )}
        </div>
        {this.state.popoverOpen &&
          (props.filterOptions && props.filterOptions.length > 0 ? (
            <OptionFilterPopover
              anchorRect={this.state.anchorRect}
              options={props.filterOptions}
              value={props.filterValue}
              onApply={this.handleOptionsApply}
              onClose={this.handlePopoverClose}
            />
          ) : props.columnType === 'dateTime' ? (
            <DateFilterPopover
              anchorRect={this.state.anchorRect}
              value={props.filterValue || ''}
              onApply={this.handleDateApply}
              onClose={this.handlePopoverClose}
            />
          ) : (
            <FilterPopover
              column={{
                index: props.columnKey,
                // The popover interpolates the name into an input placeholder,
                // so columns whose label is a JSX node supply labelText.
                name: props.labelText || props.label,
              }}
              filter={{ filter: this.state.draftValue, index: props.columnKey }}
              anchorRect={this.state.anchorRect}
              onChange={this.handlePopoverChange}
              onSubmit={this.handlePopoverSubmit}
              onClose={this.handlePopoverClose}
            />
          ))}
      </div>
    );
  }
}

InspectRecordsHeaderCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  columnType: PropTypes.string,
  label: PropTypes.node.isRequired,
  labelText: PropTypes.string,
  title: PropTypes.string,
  path: PropTypes.string,
  attributeLabel: PropTypes.string,
  showHierarchy: PropTypes.bool,
  filterable: PropTypes.bool,
  sortable: PropTypes.bool,
  filterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  filterOptions: PropTypes.array,
  sortColumnKey: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc', null]),
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

export default withTranslation()(InspectRecordsHeaderCell);
